import { faBell, faBellSlash } from "@fortawesome/pro-regular-svg-icons";
import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import isNumber from "lodash-es/isNumber";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { getFormValues } from "redux-form";

import { analyticsStoreEvent } from "@/mk2/actions";
import { contestsHomeUrl } from "@/mk2/apps/contests/urls";
import { addCounsellingQuestion, postSendHeart } from "@/mk2/apps/forum/actions";
import {
    COMMENT_CREATE_FORM_NAME,
    CommentFormData,
} from "@/mk2/apps/forum/components/CommentCreateEditForm";
import PostDetail from "@/mk2/apps/forum/containers/PostDetail/PostDetail";
import {
    postDetailAddCommentUnauthenticated,
    postDetailBlockAnonymousUserTrigger,
    postDetailBlockUserTrigger,
    postDetailCommentCreateTrigger,
    postDetailCommentDeleteTrigger,
    postDetailCommentReply,
    postDetailDeletePost,
    postDetailFetchTrigger,
    postDetailMoveUpComment,
    postDetailPendingActionCreateTrigger,
    postDetailScrollToTrigger,
    postDetailThreadBlockAnonymousUserTrigger,
    postDetailThreadBlockUserTrigger,
    postDetailToggleCommentHighlight,
    postDetailToggleNotifications,
    postDetailTrackTrigger,
} from "@/mk2/apps/forum/containers/PostDetail/PostDetail.actions";
import { POST_DETAIL_MENU_MORE } from "@/mk2/apps/forum/containers/PostDetail/PostDetail.constants";
import styles from "@/mk2/apps/forum/containers/PostDetail/PostDetail.mscss";
import postsDetailReducer, {
    PostDetailPageState,
    PostDetailVirtualPageState,
} from "@/mk2/apps/forum/containers/PostDetail/PostDetail.reducers";
import postsDetailSaga from "@/mk2/apps/forum/containers/PostDetail/PostDetail.sagas";
import {
    findPostDetailPost,
    getForumPostDetailPageState,
    getForumPostDetailState,
    getForumPostDetailVirtualPageState,
    getForumPostsDetailState,
} from "@/mk2/apps/forum/containers/PostDetail/PostDetail.selectors";
import { PostDetailRight } from "@/mk2/apps/forum/containers/PostDetail/PostDetailRight";
import { FORUM_COMMENT_MENU_MORE, postDetailUrl } from "@/mk2/apps/forum/helpers";
import { getForumNotificationsOn } from "@/mk2/apps/forum/reducers";
import {
    BrandDaysCampaignEntity,
    BrandDaysCampaignSchema,
    ContestEntity,
    ContestSchema,
    CounsellingEntity,
    CounsellingSchema,
    ForumMessageEntity,
    ForumMessageSchema,
    PageRef,
    PostEntity,
    PostSchema,
} from "@/mk2/apps/forum/schemas";
import {
    breadcrumbsForum,
    discussionFAQPage,
    discussionForumPosting,
    postOG,
} from "@/mk2/apps/forum/structuredData";
import { forumSearchUrl, forumTopicUrl } from "@/mk2/apps/forum/urls";
import { parsePageNumber } from "@/mk2/apps/forum/utils";
import { Btn, BtnLayout, BtnType } from "@/mk2/components/Btn";
import { FormPageType } from "@/mk2/constants/enums";
import { AnchorLeaderboardShowTrigger } from "@/mk2/containers/AdSlotAnchorLeaderboard/AdSlotAnchorLeaderboard";
import { complaintOpenTrigger } from "@/mk2/containers/Complaint/Complaint.actions";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import {
    getComplaintShownForForumPost,
    getComplaintsShownForForumComments,
} from "@/mk2/containers/Complaints/Complaints.selectors";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { FORUM_TOPICS_WITH_NEWSLETTER } from "@/mk2/containers/PregnancyNewsletter/PregnancyNewsletter.constants";
import {
    mkReduxFormMapDispatchToProps,
    mkReduxFormMapStateToProps,
} from "@/mk2/decorators/mkReduxForm";
import { getFirstParagraphsPlaintext } from "@/mk2/helpers/article_ast/utils";
import { cacheLast } from "@/mk2/helpers/cache";
import { showWarningToastAction } from "@/mk2/helpers/toasts";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import FormPage, { FormPageDispatchProps, FormPageStateProps } from "@/mk2/pages/FormPage";
import {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    PageRouteProps,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceIsIOS,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
    getRequestUserIsSpider,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    ContentItemEntity,
    ContentItemSchema,
    UserEntity,
    WikiArticleEntity,
    WikiArticleSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    CONFIRM,
    COUNSELLING_ANSWERED,
    COUNSELLING_IS_ARCHIVED,
    FORUM_ADD_QUESTION,
    SEO_DESCRIPTION_FORUM_POST_perex,
    SEO_TITLE_FORUM_POST_title,
    SEO_TITLE_FORUM_POST_title_AT_PAGE_pagenum,
    TURN_NOTIFICATIONS_OFF,
    TURN_NOTIFICATIONS_ON,
} from "@/mk/autogenerated/translations/PostDetailPage.c215428516effb168251449d946a9d32";
import { boldize } from "@/mk/bazaar/common/filters";
import { hasAds } from "@/mk/bazaar/common/userUtils";

export enum ScrollToPosition {
    FirstComment = "first",
    LastComment = "last",
}

// aby sme vedeli rozlisit medzi poradnami a forum uz pocas LoadingState.INIT a LoadingState.LOADING
// a zobrazovali tak spravne UI
export enum PostDetailViewType {
    Forum,
    AskAnExpert,
    Contest,
}

export interface OwnProps {}

export interface StateProps extends FormPageStateProps {
    post: PostEntity;
    isMyAnonymousPost: boolean;
    myAnonymousCommentsIds: number[];
    comments: ForumMessageEntity[];
    commentsWithComplaint: ForumMessageEntity[];
    commentsComplaintOpened: boolean[];
    pageRef: PageRef; // page number or symbolic name (e.g. 'last') of current page. Null if page=... value is invalid
    numberOfPages: number;
    isMobile: boolean;
    isIOS: boolean;
    isSpider: boolean;
    requestUserPermissions: string[];
    initialValues: CommentFormData;
    currentValues: CommentFormData;
    requestUser: UserEntity;
    loadingState: LoadingState;
    errorMessage: string;
    postComplaintOpened: boolean;
    baseUrl: string;
    notificationsOn: number[];
    commentToBeHighlighted: number | "last";

    hasBrandingOrBanner: boolean;

    similarPosts: PostEntity[];
    recentPosts: PostEntity[];
    backlinksBox: ContentItemEntity;
    viewType: PostDetailViewType;

    contest: ContestEntity | null; // ak sme v poste z topicu Sutaze, obsahuje data o aktualnej sutazi
    contestAlreadyParticipated: boolean;
    counselling: CounsellingEntity;
    brandDaysCampaign: BrandDaysCampaignEntity;
    brandDaysPosts: PostEntity[];

    promoReviewArticlesHeader: WikiArticleEntity[];
    promoReviewArticlesMiddle: WikiArticleEntity[];
    promoReviewArticlesFooter: WikiArticleEntity[];

    linkedReviewArticles: WikiArticleEntity[];
    relatedWikiArticles: WikiArticleEntity[];

    userCanHavePregnancyNewsletter: boolean;
    isTitleEditableOnceAgain: boolean;
    postCanHavePregnancyNewsletter: boolean;
    blockedUserIds: number[];
    myBlockedUserIds: number[];
    secretBlockedAnonHashes: string[];
    hiddenAnonHashes: string[];
    threadBlockedUserIds: number[];
    threadBlockedAnonHashes: string[];
}

export interface DispatchProps extends FormPageDispatchProps {
    onLoad(
        topicSlug: string,
        postSlug: string,
        page: PageRef,
        location: HistoryLocationWithState,
        numCommentsBeforeLoad: number,
    );
    onReply(
        username: string,
        textArea: HTMLTextAreaElement,
        repliedToCommentId: number,
        location: HistoryLocationWithState,
    );
    onReport(entity: ComplaintTargetType, id: number, location: HistoryLocationWithState);
    onToggleMenuMore(id: number);
    onStoreEvent(name: string, props: any);
    onAddComment(postId: number, values: CommentFormData, location: HistoryLocationWithState);
    onAddCommentUnauthenticated(location: HistoryLocationWithState);
    onPendingActionCreate(
        postId: number,
        commentFd: CommentFormData,
        location: HistoryLocationWithState,
    );
    onDeleteComment(id: number);
    onMoveUpComment(id: number);
    onScrollTo(scrollto: ScrollToPosition);

    onToggleMenuMorePostDetail();
    onToggleNotifications(
        username: string,
        channelId: number,
        follow: boolean,
        location: HistoryLocationWithState,
    );
    onDelete(id: number);

    onTrackEdit(itemId: number);
    onSendHeart(location: HistoryLocationWithState);
    onToggleCommentHighlight(key: string, commentId: number | "last");
    onShowWarningToast(message: string | React.ReactNode);
    onAddCounsellingQuestion(counselling: CounsellingEntity, location: HistoryLocationWithState);
    onBlockUser(
        userId: number,
        username: string,
        blocked: boolean,
        key: string,
        location: HistoryLocationWithState,
    );
    onBlockAnonymousUser(
        anonAuthorHash: string,
        threadId: number,
        key: string,
        location: HistoryLocationWithState,
    );
    onThreadBlockUser(
        userId: number,
        username: string,
        threadId: number,
        blocked: boolean,
        key: string,
        location: HistoryLocationWithState,
    );
    onThreadBlockAnonymousUser(
        anonAuthorHash: string,
        threadId: number,
        blocked: boolean,
        key: string,
        location: HistoryLocationWithState,
    );
}

export type RouteProps = PageRouteProps<{
    topicSlug: string;
    postSlug: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

const initialValues = {
    text: "",
    photos: [],
    sticker: null,
};

export default class PostDetailPage extends FormPage<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    private onToggleNotificationsCache = cacheLast();
    private onAddPostClicked = cacheLast();

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                location,
                routeParams: { topicSlug, postSlug },
            } = ownProps;

            const pageRef = parsePageNumber(ownProps.location);
            const postsDetailState = getForumPostsDetailState(state);
            const postDetailState = getForumPostDetailState(state, topicSlug, postSlug);
            const postDetailPageState: PostDetailPageState | PostDetailVirtualPageState = isNumber(
                pageRef,
            )
                ? getForumPostDetailPageState(state, topicSlug, postSlug, pageRef)
                : getForumPostDetailVirtualPageState(state, topicSlug, postSlug, pageRef);
            const post: PostEntity = findPostDetailPost(state, topicSlug, postSlug);

            const commentIds = (postDetailPageState as PostDetailPageState).commentIds;
            const comments =
                !isNumber(pageRef) || commentIds === null
                    ? null
                    : getDenormalizedEntities<ForumMessageEntity>(
                          state,
                          ForumMessageSchema,
                          commentIds,
                      );

            const similarPosts = getDenormalizedEntities<PostEntity>(
                state,
                PostSchema,
                postDetailState.similarPostIds,
            );
            const recentPosts = getDenormalizedEntities<PostEntity>(
                state,
                PostSchema,
                postDetailState.recentPostIds,
            );
            const backlinksBox = getDenormalizedEntity<ContentItemEntity>(
                state,
                ContentItemSchema,
                postDetailState.backlinksBoxId,
            );
            const myAnonymousCommentsIds = postDetailState.myAnonymousCommentsIds;
            const commentsWithComplaint = getDenormalizedEntities<ForumMessageEntity>(
                state,
                ForumMessageSchema,
                postDetailState.commentsWithComplaintIds,
            );
            const userCanHavePregnancyNewsletter = postsDetailState.userCanHavePregnancyNewsletter;
            const postCanHavePregnancyNewsletter =
                post?.topic?.slug && FORUM_TOPICS_WITH_NEWSLETTER.includes(post.topic.slug);
            const isTitleEditableOnceAgain = postDetailState.isTitleEditableOnceAgain;

            const mkReduxFormStateProps =
                mkReduxFormMapStateToProps(COMMENT_CREATE_FORM_NAME)(state);
            const hasBrandingOrBanner = this.getHasBrandingOrBanner(
                post,
                mkReduxFormStateProps.currentFormPage,
                location,
            );

            const isMyAnonymousPost = postDetailState.isMyAnonymousPost;

            return {
                ...mkReduxFormStateProps,
                viewType: PostDetailViewType.Forum,
                post,
                isMyAnonymousPost,
                myAnonymousCommentsIds,
                comments,
                commentsWithComplaint,
                pageRef,
                numberOfPages: postDetailState.numberOfPages,
                loadingState: postDetailPageState.loadingState,
                errorMessage: postDetailPageState.errorMessage,
                isMobile: getRequestDeviceMobile(state),
                isIOS: getRequestDeviceIsIOS(state),
                isSpider: getRequestUserIsSpider(state),
                initialValues,
                currentValues: getFormValues(COMMENT_CREATE_FORM_NAME)(state) as CommentFormData,
                requestUserPermissions: getRequestPermissions(state),
                requestUser: getRequestUser(state),
                postComplaintOpened: getComplaintShownForForumPost(state, post),
                commentsComplaintOpened: getComplaintsShownForForumComments(state, comments),
                baseUrl: getRequestBaseUrl(state),
                notificationsOn: getForumNotificationsOn(state),
                commentToBeHighlighted: postDetailState.commentToBeHighlighted,

                hasBrandingOrBanner,

                similarPosts,
                recentPosts,
                backlinksBox,

                contest: getDenormalizedEntity<ContestEntity>(
                    state,
                    ContestSchema,
                    postDetailState.contestId,
                ),
                contestAlreadyParticipated: postDetailState.contestAlreadyParticipated,
                counselling: getDenormalizedEntity<CounsellingEntity>(
                    state,
                    CounsellingSchema,
                    postDetailState.counselling,
                ),
                brandDaysCampaign: getDenormalizedEntity<BrandDaysCampaignEntity>(
                    state,
                    BrandDaysCampaignSchema,
                    postDetailState.brandDaysCampaignId,
                ),
                brandDaysPosts: getDenormalizedEntities<PostEntity>(
                    state,
                    PostSchema,
                    postDetailState.brandDaysPostsIds,
                ),

                promoReviewArticlesHeader: getDenormalizedEntities<WikiArticleEntity>(
                    state,
                    WikiArticleSchema,
                    postDetailState.promoReviewArticlesHeaderIds,
                ),
                promoReviewArticlesMiddle: getDenormalizedEntities<WikiArticleEntity>(
                    state,
                    WikiArticleSchema,
                    postDetailState.promoReviewArticlesMiddleIds,
                ),
                promoReviewArticlesFooter: getDenormalizedEntities<WikiArticleEntity>(
                    state,
                    WikiArticleSchema,
                    postDetailState.promoReviewArticlesFooterIds,
                ),

                linkedReviewArticles: getDenormalizedEntities<WikiArticleEntity>(
                    state,
                    WikiArticleSchema,
                    postDetailState.linkedReviewArticleIds,
                ),

                relatedWikiArticles: getDenormalizedEntities<WikiArticleEntity>(
                    state,
                    WikiArticleSchema,
                    postDetailState.relatedWikiArticleIds,
                ),

                userCanHavePregnancyNewsletter,
                postCanHavePregnancyNewsletter,
                isTitleEditableOnceAgain,
                blockedUserIds: postDetailState.blockedUserIds || [],
                myBlockedUserIds: postDetailState.myBlockedUserIds || [],
                secretBlockedAnonHashes: postDetailState.secretBlockedAnonHashes || [],
                hiddenAnonHashes: postDetailState.hiddenAnonHashes || [],
                threadBlockedUserIds: postDetailState.threadBlockedUserIds || [],
                threadBlockedAnonHashes: postDetailState.threadBlockedAnonHashes || [],
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            ...mkReduxFormMapDispatchToProps(COMMENT_CREATE_FORM_NAME),
            onLoad: postDetailFetchTrigger,
            onReply: postDetailCommentReply,
            onStoreEvent: analyticsStoreEvent,
            onReport: complaintOpenTrigger,
            onToggleMenuMore: (id: number) => menuToggle(FORUM_COMMENT_MENU_MORE(id)),
            onAddComment: postDetailCommentCreateTrigger,
            onAddCommentUnauthenticated: postDetailAddCommentUnauthenticated,
            onPendingActionCreate: postDetailPendingActionCreateTrigger,
            onDeleteComment: postDetailCommentDeleteTrigger,
            onMoveUpComment: postDetailMoveUpComment,
            onScrollTo: postDetailScrollToTrigger,
            onToggleMenuMorePostDetail: () => menuToggle(POST_DETAIL_MENU_MORE),
            onToggleNotifications: (
                username: string,
                channelId: number,
                follow: boolean,
                location: HistoryLocationWithState,
            ) => postDetailToggleNotifications(username, "thread", channelId, follow, location),
            onDelete: postDetailDeletePost,
            onTrackEdit: postDetailTrackTrigger,
            onSendHeart: postSendHeart,
            onToggleCommentHighlight: postDetailToggleCommentHighlight,
            onShowWarningToast: showWarningToastAction,
            onAddCounsellingQuestion: addCounsellingQuestion,
            onBlockUser: postDetailBlockUserTrigger,
            onBlockAnonymousUser: postDetailBlockAnonymousUserTrigger,
            onThreadBlockUser: postDetailThreadBlockUserTrigger,
            onThreadBlockAnonymousUser: postDetailThreadBlockAnonymousUserTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const { post, pageRef, counselling } = props;

        let title = null;
        if (post && isNumber(pageRef)) {
            const plainHeading = boldize(post.heading, true)[0] as string;
            title =
                pageRef > 1
                    ? interpolate(SEO_TITLE_FORUM_POST_title_AT_PAGE_pagenum, {
                          title: plainHeading,
                          pagenum: pageRef,
                      })
                    : interpolate(SEO_TITLE_FORUM_POST_title, { title: plainHeading });
            if (counselling && post.comments) {
                title = `[${COUNSELLING_ANSWERED}] ${title}`;
            }
        }

        return title;
    }

    public getMetaDescription(props: Props): string {
        const { post } = props;
        return post
            ? interpolate(SEO_DESCRIPTION_FORUM_POST_perex, {
                  perex: getFirstParagraphsPlaintext(post.perexAST),
              })
            : null;
    }

    public getOpenGraphData(
        props: Props,
        baseUrl: string,
        currentUrl: string,
    ): { [key: string]: string } {
        const { post } = props;

        if (post) {
            return {
                ...super.getOpenGraphData(props, baseUrl, currentUrl),
                ...postOG(baseUrl, post),
            };
        }
    }

    public getStructuredData(props: Props): object[] {
        const { post, baseUrl, comments, linkedReviewArticles } = props;
        if (post) {
            const topic = post.topic ? post.topic : null;
            const faqPage = discussionFAQPage(baseUrl, post);
            return [
                breadcrumbsForum(baseUrl, undefined, topic, post),
                discussionForumPosting(baseUrl, post, comments, linkedReviewArticles),
                ...(faqPage ? [faqPage] : []),
            ];
        }
    }

    public getMetaRobots(props: Props): string {
        const { post } = props;
        const indexDirective = post && post.noindex ? "noindex,nofollow" : "index,follow";
        return `${indexDirective}, max-image-preview:large`;
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        const { pageRef, numberOfPages, post } = props;

        const hasPageNums: boolean =
            numberOfPages !== null && pageRef !== null && isNumber(pageRef);
        if (hasPageNums && post) {
            return baseUrl + postDetailUrl(post);
        }
    }

    public render(props: Props): false | JSX.Element {
        return <PostDetail {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return <PostDetailRight {...props} />;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const {
            routeParams: { topicSlug },
        } = props;
        return url(forumTopicUrl, { topicSlug });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        // Halfpage ad moved inline to PostDetailRight (StickyBlock 2).
        return null;
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { isMobile, comments, post, currentFormPage, requestUser } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if (!post || post.deletedAt || post.isNoAds) {
            return null;
        }

        if (!post || (comments?.length || 0) < 8) {
            return null;
        }

        if (currentFormPage === FormPageType.Photos || currentFormPage === FormPageType.Stickers) {
            return null;
        }

        if (post.topic.isCommercial || post.topic.isBrandDays) {
            return null;
        }

        return {
            zone: "Forum - Anchor - Mobile",
            showTrigger: AnchorLeaderboardShowTrigger.OnScroll,
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        const { hasBrandingOrBanner, isMobile, location } = props;

        if (hasBrandingOrBanner && !isMobile) {
            return { zone: "Forum - Top 01 - Desktop" };
        }
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Forum - Interstitial" };
    }

    public getHasBrandingOrBanner(post, currentFormPage, location): boolean {
        // return BarandingProps even if post is not loaded, except contests / brand-days
        if (!post) {
            return !(
                location.pathname.startsWith(contestsHomeUrl) ||
                location.pathname.includes("/dni-s")
            );
        }

        if (post.deletedAt || post.isNoAds) {
            return false;
        }

        if (currentFormPage === FormPageType.Photos || currentFormPage === FormPageType.Stickers) {
            return false;
        }

        return !(post.topic.isCommercial || post.topic.isBrandDays);
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const { currentFormPage, onCloseFormPage } = props;
        return currentFormPage === FormPageType.Main ? (
            this.getHeaderRightOnMainFormPage(props)
        ) : (
            <Btn label={CONFIRM} onClick={onCloseFormPage} />
        );
    }

    public getHeaderRightOnMainFormPage(props: Props): React.ReactElement {
        const {
            post,
            onToggleNotifications,
            onAddCounsellingQuestion,
            notificationsOn,
            requestUser,
            counselling,
            viewType,
            isMobile,
            location,
        } = props;

        if (!post || post.deletedAt || post.topic.deletedAt) {
            return null;
        }

        if (viewType === PostDetailViewType.Contest) {
            return null;
        } else if (viewType === PostDetailViewType.AskAnExpert) {
            return (
                counselling &&
                !counselling.isArchived &&
                !counselling.isLocked && (
                    <Btn
                        type={BtnType.Blue}
                        label={
                            !counselling.isArchived ? FORUM_ADD_QUESTION : COUNSELLING_IS_ARCHIVED
                        }
                        disabled={counselling.isArchived}
                        onClick={this.onAddPostClicked(() => {
                            onAddCounsellingQuestion(counselling, location);
                        }, `${counselling.id}`)}
                    />
                )
            );
        } else {
            const follow: boolean = notificationsOn.indexOf(post.id) > -1;

            return (
                <div className={styles.PostDetail__searchAndNotification}>
                    {isMobile && (
                        <Btn
                            className={styles.PostDetail__searchAndNotification__searchIcon}
                            type={BtnType.BlueOutline}
                            icon={faSearch}
                            link={forumSearchUrl}
                            aria-label="Search"
                        />
                    )}
                    <Btn
                        type={BtnType.BlueOutline}
                        layout={BtnLayout.LabelIcon}
                        onClick={this.onToggleNotificationsCache(
                            () => {
                                onToggleNotifications(
                                    requestUser.username,
                                    post.id,
                                    !follow,
                                    location,
                                );
                            },
                            requestUser.username,
                            post.id,
                            follow,
                        )}
                        icon={follow ? faBellSlash : faBell}
                        label={follow ? TURN_NOTIFICATIONS_OFF : TURN_NOTIFICATIONS_ON}
                    />
                </div>
            );
        }
    }

    public getSubmitButton(props: Props): React.ReactElement {
        /* not used submit button */
        return null;
    }

    public hasMobileLogo(props: Props): boolean {
        const { viewType } = props;

        // v poradni sa logo prekryva s dlhym action buttonom
        return viewType === PostDetailViewType.Contest ||
            viewType === PostDetailViewType.AskAnExpert
            ? super.hasMobileLogo(props)
            : // vo fore pristava vela ludi zo searchu, kvoli brandingu tam chceme mat logo vzdy,
              // aj ked je vpravu nejaky button vpravo
              true;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.forum.forumPostDetail", postsDetailReducer);
        store.injectSaga("containers.forum.forumPostDetail", postsDetailSaga);
    }
}
